import React, { type FC } from 'react';
import styled from 'styled-components';
import Breadcrumbs, { type BreadcrumbsProps } from './breadcrumbs';
import Button from './button';
import Container from './container';
import { STANDARD_PADDING } from '../constants';
import Title from './title';
import { Grid, Paragraph } from './UIKit/system';
import { getThemeColor } from '../utils/styles';
import { CoursePopupButton } from './course-popup-button';

interface Intro7Props {
  pagination: BreadcrumbsProps['items'];
  title: string;
  body?: string;
  infoContent?: {
    title: string;
    text: string;
  }[];
  cta: {
    title: string;
    body: string;
    links: {
      to: string;
      title: string;
      icon?: string;
      course?: boolean;
      description?: string;
    }[];
  };
}

const Intro7: FC<Intro7Props> = (props) => {
  const { pagination, title, body, infoContent, cta } = props;
  const hasMoreLinks = cta.links.length > 1;

  return (
    <Container pt={STANDARD_PADDING} pb={40} relative zIndex={1}>
      <Grid gridAutoFlow={['row', 'column']} gridGap={[0, 30, 130]}>
        <div>
          <Breadcrumbs ml={2} mb={14} items={pagination} />
          <Title as="h1" mb={20} lineHeight={1.1} maxWidth={765} html={title} />
          {body && <Paragraph html={body} grey />}
          {infoContent && (
            <Grid
              gridAutoFlow={['row', 'column']}
              mt={32}
              mb={[0, 32]}
              gridGap={[0, 32]}
            >
              {infoContent.map((content, index) => (
                <div key={content.title + index}>
                  <Paragraph html={content.title} fontSize={16} bold />
                  <Paragraph html={content.text} fontSize={16} grey />
                </div>
              ))}
            </Grid>
          )}
        </div>
        <CTA mb={[50, hasMoreLinks ? -100 : 0]} style={{ borderRadius: 16 }}>
          <Title
            as="p"
            color="white"
            fontSize={[30, 26, 32]}
            whiteSpace="pre-wrap"
          >
            {cta.title}
          </Title>
          <Paragraph mb={hasMoreLinks ? 30 : 0} lightGrey>
            {cta.body}
          </Paragraph>
          <Grid gridGap={16}>
            {cta.links.map((link) =>
              link.course ? (
                <CoursePopupButton key={link.to} to={link.to} inDarkBlock>
                  {link.title}
                </CoursePopupButton>
              ) : (
                <StyledButton {...link} key={link.to} inDarkBlock>
                  {link.title}
                </StyledButton>
              )
            )}
          </Grid>
        </CTA>
      </Grid>
    </Container>
  );
};

const CTA = styled(Grid).attrs(() => ({
  alignSelf: 'start',
  mt: [30, 40],
  width: [undefined, 350]
}))`
  background-color: ${getThemeColor('black')};
  gap: 16px;
  grid-template-rows: auto 1fr;
  padding: 32px;
`;

const StyledButton = styled(Button)`
  svg {
    height: 50%;
  }
`;

export default Intro7;
